import React, { ReactElement, useEffect, useState, useRef } from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Checkbox from '@material-ui/core/Checkbox';
import CloseIcon from '@material-ui/icons/Close';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CTA from 'components/UI/Cta';
import { VtoSteps, clearVtoScanData } from './VTO';
import FittingboxVTO from './FittingboxVTO';
import Link from 'components/UI/Link';

import styles from './styles.module.css';
import {
  addItemToLocalStorage,
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
} from 'lib/util';

const DESKTOP_WIDTH = '1024px';
const VTO_AGREED_KEY = 'vto_agreed';

type ProductVtoProps = {
  glassesId: string;
  fullScreen?: boolean;
  onCtaClick?: () => void;
  onClose?: () => void;
  siteName: string;
};

/**
 * Scroll taking header element into account.
 * @param element
 */
const scroll = (element: HTMLElement): void => {
  const yOffset = document.querySelector('header')?.offsetHeight || 0;
  const y = element.getBoundingClientRect().top - yOffset + window.pageYOffset;
  window.scroll({ top: y, behavior: 'smooth' });
};

export default function ProductVto({
  glassesId,
  fullScreen = false,
  onCtaClick = () => {
    return;
  },
  onClose = () => {
    return;
  },
  siteName,
}: ProductVtoProps): ReactElement {
  const [vtoStep, setVtoStep] = useState<VtoSteps>('init');
  const [vtoAgreed, setVtoAgreed] = useState(
    Boolean(JSON.parse(getItemFromLocalStorage(VTO_AGREED_KEY) || 'null'))
  );
  const [vtoActive, setVtoActive] = useState(false);
  const isDesktop = useMediaQuery(`(min-width: ${DESKTOP_WIDTH})`);
  const vtoBlockRef = useRef<HTMLDivElement | null>(null);
  const vtoContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isDesktop) {
      // scroll to the vtoBlock
      if (vtoBlockRef.current) scroll(vtoBlockRef.current);
    } else {
      // on mobile, scroll all the way to vtoContainer w/overlay
      if (vtoAgreed && vtoContainerRef.current) scroll(vtoContainerRef.current);
      else if (vtoBlockRef.current) scroll(vtoBlockRef.current);
    }
    if (vtoAgreed) setVtoActive(true);
  }, [isDesktop]);

  useEffect(() => {
    if (vtoStep === 'rescan') {
      setVtoActive(false);
      setVtoAgreed(false);
    }
  }, [vtoStep]);

  useEffect(() => {
    addItemToLocalStorage(VTO_AGREED_KEY, JSON.stringify(vtoAgreed));
  }, [vtoAgreed]);

  const formLabelStyles = makeStyles({
    label: {
      fontSize: '0.65rem',
    },
  })();

  const fsClasses = fullScreen ? 'h-screen md:h-auto mb-0 py-12' : 'py-4 mb-5';

  return (
    <div
      ref={vtoBlockRef}
      className={`relative flex flex-col md:flex-row px-4 md:pt-8 md:mb-10 bg-gray-300 overflow-y-auto ${fsClasses}`}
    >
      <button
        className="md:absolute top-5 right-5 text-right"
        onClick={() => {
          onClose();
        }}
      >
        <CloseIcon /> CLOSE
      </button>
      <div className="order-last md:order-first md:w-1/2 px-5 py-4 sm:px-10 sm:py-8 md:px-20 md:py-16">
        <div
          id="vto-title"
          className="text-center md:text-left text-3xl md:text-4xl font-bold mb-5 md:mb-10"
        >
          Virtual Try-On
        </div>
        <div id="vto-description" className="text-center md:text-left md:text-lg mb-5 md:mb-0">
          Our virtual tools allows you to try on all of our styles using your phone or webcam.
        </div>
        <ul className={`mb-5 md:mb-0 md:text-lg ${styles.big_bullets}`}>
          {['init', 'scan', 'rescan'].includes(vtoStep) && (
            <>
              <li>Click &quot;I agree&quot; to start</li>
              <li>
                <span className="inline">
                  Find the{' '}
                  <Link slug="/locations">
                    <a className="text-tertiary-actual underline">nearest {siteName} office </a>
                  </Link>
                  to get your new prescription and eyeglasses today!
                </span>
              </li>
            </>
          )}
        </ul>
        {!vtoActive && (
          <div className="block md:hidden">
            <CTA
              disabled={!vtoAgreed}
              text="Start Try-On"
              colorScheme="Primary"
              slug=""
              onClick={() => {
                setVtoActive(true);
                onCtaClick();
              }}
            />
            <div className="flex justify-center mt-2">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={vtoAgreed}
                    onChange={(e) => setVtoAgreed(e.target.checked)}
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<RadioButtonCheckedIcon />}
                  />
                }
                classes={formLabelStyles}
                label="I agree to the privacy terms below."
              />
            </div>
          </div>
        )}
        <p className={`${styles.privacy} mt-2`}>
          <strong>Privacy</strong> | When you use our virtual try-on service, we collect biometric
          information about your face shape. This information is not used for any other purpose
          other than to show the glasses on you. By using this try-on service, you give us consent
          to collect this data. You can delete your try-on data at any time by{' '}
          <button
            className="font-bold"
            onClick={async () => {
              await clearVtoScanData();
              setVtoActive(false);
              setVtoAgreed(false);
              setVtoStep('init');
              removeItemFromLocalStorage(VTO_AGREED_KEY);
            }}
          >
            clicking here
          </button>
          .
        </p>
      </div>
      <div ref={vtoContainerRef} className="flex md:items-center md:justify-center md:w-1/2">
        {vtoAgreed && vtoActive ? (
          <FittingboxVTO
            apiKey={process.env.NEXT_PUBLIC_FITTINGBOX_API_KEY}
            glassesId={glassesId}
          />
        ) : (
          <div className="hidden md:flex md:flex-col">
            <CTA
              disabled={!vtoAgreed}
              text="Start Try-On"
              colorScheme="Primary"
              slug=""
              onClick={() => {
                setVtoActive(true);
                onCtaClick();
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={vtoAgreed}
                  onChange={(e) => setVtoAgreed(e.target.checked)}
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<RadioButtonCheckedIcon />}
                />
              }
              label="I agree to the privacy terms below the instructions."
            />
          </div>
        )}
      </div>
    </div>
  );
}
